import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { PLATFORM_ID, inject, isDevMode, REQUEST } from '@angular/core';
import { Response } from 'express';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CookieService, SeoMetaService } from '@app/core';
import { RESPONSE } from 'src/server/express/express.token';

export const httpInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
  const seoMetaService: SeoMetaService = inject(SeoMetaService);
  const response: Response = inject(RESPONSE, { optional: true });
  const request = inject(REQUEST, { optional: true });
  const platformId: Object = inject(PLATFORM_ID);
  const cookieService: CookieService = inject(CookieService, { optional: true })!;

  let device = 'DESKTOP';
  let userAgent = request?.headers?.['user-agent'] ?? '';

  let headers: any = {};
  if (isPlatformBrowser(platformId)) {
    userAgent = window.navigator.userAgent;
    headers = req?.headers?.['headers'] ?? {};
  } else {
    headers = request?.headers ?? {};
    headers['Cookie'] = request?.headers?.['cookie'] ?? '';
  }

  if (isMobile(userAgent)) {
    device = 'MOBILE';
  }

  if (isDevMode()) {
    headers = {
      ...headers,
      'is-dev-mode': 'true',
      'dev-sessionid': '3xJ2ZKAEzGJo2WB',
    };
  }

  const jwtToken = cookieService?.getCookie('ib_jwt');
  if (jwtToken) {
    headers['x-ib-authorization'] = `Bearer ${jwtToken}`;
  }

  headers = {
    ...headers,
    'X-REQUESTED-WITH': 'XMLHttpRequest',
    'X-Forward-To': 'node',
    'x-ib-client': device,
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  };

  const modified = req.clone({
    url: req.url,
    withCredentials: true,
    setHeaders: headers,
  });

  return next(modified).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          let apiResponse: any = event;

          if (!req.url?.includes('users/me')) {
            const seoData = apiResponse?.body?.RESPONSE?.pageData?.seoData;
            if (!apiResponse?.body?.RESPONSE?.pageData?.skipSeoTags) {
              seoMetaService.setSeoTags(seoData?.seo);
            }
          }
          if (isPlatformServer(platformId)) {
            const headers = new Headers();
            apiResponse?.headers?.headers?.forEach(
              (header: string, key: string) => {
                let excludedHeaders = [
                  'content-encoding',
                  'content-type',
                  'transfer-encoding',
                ];
                if (!excludedHeaders.includes(key)) {
                  headers.append(key, header);
                }
              },
            );
            if (response) {
              response.setHeaders(headers);
            }
          }

          if ([200, 201, 202].includes(apiResponse.status)) {
            if ([301, 302].includes(apiResponse?.body?.STATUS_CODE)) {
              const redirectUrl: string =
                apiResponse?.body?.RESPONSE?.redirectionUrl ?? '/';
              const redirectCode: number = Number(
                apiResponse?.body?.STATUS_CODE,
              );
              if (response) {
                response.redirect(redirectCode, redirectUrl);
              } else if (isPlatformBrowser(platformId)) {
                window.location.replace(redirectUrl);
              }
            } else if (response && apiResponse?.body?.STATUS_CODE === 404) {
              response.statusCode = 404;
            }
          }
        }
      },
      error: (err) => {
        if (err instanceof HttpErrorResponse) {
          if ([301, 302].includes(err.status)) {
            const redirectUrl = err.error.redirect;
            const redirectCode: number = Number(err.error.status_code);
            if (response) {
              response.redirect(redirectCode, redirectUrl);
            } else if (isPlatformBrowser(platformId)) {
              window.location.replace(redirectUrl);
            }
          } else if (response && err.status === 404) {
            response.statusCode = 404;
          }
          return;
        }
      },
      complete: () => {
        if (isDevMode()) {
          console.log('Request completed');
        }
      },
    }),
  );
};

const isMobile = (userAgent: string) => {
  const regex: RegExp =
    /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  return regex.test(userAgent);
};
